<template>
  <page>
    <v-card>
      <v-card-title>
        <v-text-field append-icon="mdi-magnify" label="Search" single-line hide-details v-model="search"></v-text-field>
        <v-spacer></v-spacer>
        <v-btn color="primary" dark class=" mr-2" @click="doNew">
          New Fund
          <v-icon right>mdi-plus</v-icon>
        </v-btn>
        <download-csv :data="items" name="FundList.csv">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn class="mx-2" fab dark small color="primary" v-on="on">
                <v-icon dark>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>Download CSV</span>
          </v-tooltip>
        </download-csv>
        <v-flex xs12>
          <message :messages="messages"></message>
        </v-flex>
      </v-card-title>
      <v-flex xs12>
        <v-card>
          <v-data-table
            :headers="headers"
            :items="items"
            :search="search"
            :footer-props="{'items-per-page-options': itemsPerPage }"
            v-bind:options.sync="pagination"
            item-key="name"
            :loading="loading"
            :no-data-text="noDataText"
            :custom-filter="customSearch"
          >
            <template v-slot:item="row"
              ><tr>
                <td class="text-xs-left">{{ row.item.fund }}</td>
                <td class="text-xs-left">{{ row.item.fundDesc }}</td>
                <td class="table-action-header">
                  <table-action
                    :rowData="row"
                    review
                    @review="doReview(row.item.fund)"
                    query
                    @query="doQuery(row.item.fund)"
                    remove
                    @remove="doPromptRemove(row.item)"
                  ></table-action>
                </td></tr
            ></template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-card>
    <v-dialog v-model="fundReview" width="800px" persistent>
      <v-card
        ><v-toolbar v-if="reviewMode" dense color="grey lighten-3" flat>
          <v-icon>mdi-pencil</v-icon>
          <v-toolbar-title>Edit Fund </v-toolbar-title>
        </v-toolbar>
        <v-toolbar v-if="queryMode" dense color="grey lighten-3" flat>
          <v-icon>mdi-magnify</v-icon>
          <v-toolbar-title>Query Fund </v-toolbar-title>
        </v-toolbar>
        <v-toolbar v-if="newFund" dense color="grey lighten-3" flat>
          <v-icon>mdi-plus</v-icon>
          <v-toolbar-title>Create New Fund</v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <message :messages="messages"></message>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs6 v-if="newFund">
                <v-text-field
                  :label="labels.fund"
                  v-model="fundForm.fund"
                  :error-messages="errors.collect('fund')"
                  data-vv-name="fund"
                  :data-vv-as="labels.fund"
                  v-validate="'required|param|max:3'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ labels.fund }}</v-subheader>
                <div class="queryValue">{{ fundForm.fund }}</div>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                  :readonly="queryMode"
                  :label="labels.fundDesc"
                  v-model="fundForm.fundDesc"
                  :error-messages="errors.collect('fundDesc')"
                  data-vv-name="fundDesc"
                  :data-vv-as="labels.fundDesc"
                  v-validate="'required|max:40'"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
                <div class="queryValue">{{ fundForm.createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
                <div class="queryValue">{{ fundForm.createdDate | formatDate }}</div>
              </v-flex>
            </v-layout>
            <v-layout wrap v-if="!newFund">
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ fundForm.modifiedBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ fundForm.modifiedDate | formatDate }}</div>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click.native="doClose">Cancel</v-btn>
          <v-btn v-show="!queryMode" color="primary" text @click.native="doSave">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <confirm v-model="promptRemove" @ok="doDelete">
      <p>Are you sure you want to delete the selected Fund?</p>
      <div class="queryValue">{{ selectedFund.fund }} - {{ selectedFund.fundDesc }}</div>
    </confirm>
  </page>
</template>

<script>
import * as TableConst from '@/mixins/table'
import MessageMixin from '@/mixins/message'
import { expandURI } from '@/utils/ajax'
import { formatDisplayDate } from '@/utils/date'

export default {
  data() {
    return {
      headers: [{ text: 'Fund', value: 'fund' }, { text: 'Description', value: 'fundDesc' }, TableConst.tableActionHeader],
      fundReview: false,
      newFund: false,
      labels: {
        fund: 'Fund',
        fundDesc: 'Description',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      fundForm: {
        fund: null,
        fundDesc: null,
        createdBy: null,
        createdDate: null,
        modifiedBy: null,
        modifiedDate: null
      },
      selectedFund: {
        fund: null,
        fundDesc: null
      },
      promptRemove: false,
      queryMode: false,
      reviewMode: false,
      pagination: {
        sortDesc: [false],
        page: 1
      }
    }
  },
  created() {
    this.doInit()
  },
  methods: {
    doInit() {
      const apiURI = this.menu.fundList
      if (apiURI) {
        this.loading = true
        this.$http
          .get(apiURI)
          .then((result) => {
            this.items = []
            const { data } = result.data
            if (data) {
              data.map((fund) => {
                const convertList = {
                  fund: fund.fund,
                  fundDesc: fund.fundDesc
                }
                this.items.push(convertList)
              })
            }
            this.loading = false
          })
          .catch((err) => {
            this.loading = false
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          })
      }
    },
    doNew() {
      this.resetValidator()
      this.resetMessages()
      this.fundForm.createdBy = this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName
      this.fundForm.createdDate = new Date()
      this.fundForm.modifiedBy = null
      this.fundForm.modifiedDate = null
      this.newFund = true
      this.queryMode = false
      this.fundReview = true
      this.reviewMode = false
    },
    doReview(fund) {
      this.doFetchData(fund)
      this.newFund = false
      this.reviewMode = true
      this.queryMode = false
    },
    doQuery(fund) {
      this.doFetchData(fund)
      this.newFund = false
      this.queryMode = true
      this.reviewMode = false
    },
    doFetchData(fund) {
      this.resetValidator()
      this.resetMessages()
      if (fund) {
        this.newFund = false
        const apiURI = this.menu.fund
        if (apiURI) {
          this.$http
            .get(expandURI(apiURI, { fund }))
            .then((result) => {
              const { data } = result.data
              if (data) {
                this.fundForm.fund = data.fund.fund
                this.fundForm.fundDesc = data.fund.fundDesc
                this.fundForm.createdBy = data.createdBy
                this.fundForm.createdDate = data.createdDate
                this.fundForm.modifiedBy = data.modifiedBy
                this.fundForm.modifiedDate = data.modifiedDate
                this.fundReview = true
              }
            })
            .catch((err) => {
              const { data } = err.response
              if (data) {
                this.message(data)
                this.fieldMessage(data)
              }
            })
        }
      }
    },
    doSave() {
      this.resetMessages()
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newFund) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    doCreate() {
      const apiURI = this.menu.createFund
      if (apiURI) {
        this.$http
          .post(apiURI, this.fundForm)
          .then((result) => {
            const { data } = result
            if (data) {
              this.notify(data)
              this.doInit()
              this.doClose()
            }
          })
          .catch((err) => {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          })
      }
    },
    doUpdate() {
      const apiURI = this.menu.updateFund
      if (apiURI) {
        this.$http
          .put(apiURI, this.fundForm)
          .then((result) => {
            const { data } = result
            if (data) {
              this.notify(data)
              this.doInit()
              this.doClose()
            }
          })
          .catch((err) => {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          })
      }
    },
    async doPromptRemove(fund) {
      this.resetMessages()
      if (fund && fund.fund) {
        try {
          const apiURI = this.menu.checkDeleteFund
          const expand = {
            fund: fund.fund
          }
          await this.$http.get(expandURI(apiURI, expand))
          this.selectedFund.fund = fund.fund
          this.selectedFund.fundDesc = fund.fundDesc
          this.promptRemove = true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.fieldMessage(data)
            this.notify(data)
          }
        }
      }
    },
    doDelete() {
      this.resetMessages()
      if (this.selectedFund && this.selectedFund.fund) {
        const apiURI = this.menu.deleteFund
        if (apiURI) {
          const expand = {
            fund: this.selectedFund.fund
          }
          this.$http
            .delete(expandURI(apiURI, expand))
            .then((result) => {
              const data = result.data
              this.notify(data)
              this.promptRemove = false
              this.doInit()
            })
            .catch((err) => {
              this.promptRemove = false
              const { data } = err.response
              if (data) {
                this.message(data)
              }
            })
        }
      }
    },
    doClose() {
      this.resetValidator()
      this.resetMessages()
      this.fundForm.fund = null
      this.fundForm.fundDesc = null
      this.fundReview = false
      setTimeout(() => {
        this.newFund = false
      }, 200)
    }
  },
  computed: {
    menu() {
      return this.$store.state.fundParam.menu
    }
  },
  filters: {
    formatDate(value) {
      return formatDisplayDate(value)
    }
  },
  mixins: [TableConst.default, MessageMixin]
}
</script>

<style>
</style>
